import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@cloudscape-design/components/modal";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Box from "@cloudscape-design/components/box";
import Header from "@cloudscape-design/components/header";
import Container from "@cloudscape-design/components/container";
import KeyValuePairs from "@cloudscape-design/components/key-value-pairs";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Button from "@cloudscape-design/components/button";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import Badge from "@cloudscape-design/components/badge";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Textarea from "@cloudscape-design/components/textarea";
import Alert from "@cloudscape-design/components/alert";
import DatePicker from "@cloudscape-design/components/date-picker";
import CONFIG from "../config";

function DocumentDetails({ visible, document, onDismiss, idToken }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [fullDocument, setFullDocument] = useState(null);
  const [processingResults, setProcessingResults] = useState([]);
  const [documentTypes, setDocumentTypes] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [saveAlert, setSaveAlert] = useState(null);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.apiUrl}document-types`, {
        headers: { Authorization: idToken },
      });
      if (response.ok) {
        const data = await response.json();
        const typesMap = {};
        data.document_types.forEach((type) => {
          typesMap[type.id] = type.name;
        });
        setDocumentTypes(typesMap);
      }
    } catch (error) {
      console.error("Failed to load document types:", error);
    }
  }, [idToken]);

  const loadFullDocument = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFIG.apiUrl}documents/${document.id}`, {
        headers: { Authorization: idToken },
      });
      if (response.ok) {
        const data = await response.json();
        setFullDocument(data.document);
      }
    } catch (error) {
      console.error("Failed to load document details:", error);
    } finally {
      setLoading(false);
    }
  }, [document, idToken]);

  const loadProcessingResults = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.apiUrl}documents/${document.id}/results`, {
        headers: { Authorization: idToken },
      });
      if (response.ok) {
        const data = await response.json();
        setProcessingResults(data.results || []);
      }
    } catch (error) {
      console.error("Failed to load processing results:", error);
    }
  }, [document, idToken]);

  useEffect(() => {
    if (visible && document) {
      loadFullDocument();
      loadProcessingResults();
      loadDocumentTypes();
    }
  }, [visible, document, loadFullDocument, loadProcessingResults, loadDocumentTypes]);

  // Reset edit mode and edited data when document changes or modal closes
  useEffect(() => {
    // Reset edit mode and data when modal closes or document changes
    setEditMode(false);
    setEditedData({});
    setSaveAlert(null);
  }, [visible, document]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    return new Date(isoDate).toLocaleString();
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return t("venue.documents.details.notFound") || "Not found";
    }
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getStatusIndicator = (status) => {
    const statusMap = {
      pending: { type: "pending", text: t("venue.documents.details.statusPending") },
      processing: { type: "in-progress", text: t("venue.documents.details.statusProcessing") },
      completed: { type: "success", text: t("venue.documents.details.statusCompleted") },
      failed: { type: "error", text: t("venue.documents.details.statusFailed") },
    };
    const config = statusMap[status] || { type: "info", text: status };
    return <StatusIndicator type={config.type}>{config.text}</StatusIndicator>;
  };

  const handleEditData = () => {
    const latestResult = processingResults[0];
    if (latestResult?.extracted_data) {
      setEditedData({ ...latestResult.extracted_data });
      setEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedData({});
  };

  const isDateField = (key, value) => {
    // Check if field name suggests it's a date
    const dateKeywords = ["date", "time", "timestamp", "created", "updated", "expires", "due"];
    const keyLower = key.toLowerCase();
    const hasDateKeyword = dateKeywords.some((keyword) => keyLower.includes(keyword));

    // Check if value looks like a date (ISO format or common date formats)
    const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
    const looksLikeDate = typeof value === "string" && datePattern.test(value);

    return hasDateKeyword || looksLikeDate;
  };

  const formatDateForPicker = (value) => {
    // Convert ISO datetime to YYYY-MM-DD format for DatePicker
    if (typeof value === "string" && value.includes("T")) {
      return value.split("T")[0];
    }
    return value;
  };

  const handleSaveData = async () => {
    setLoading(true);
    setSaveAlert(null);
    try {
      const latestResult = processingResults[0];
      const response = await fetch(`${CONFIG.apiUrl}documents/${document.id}/results/${latestResult.result_id}`, {
        method: "PUT",
        headers: {
          Authorization: idToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          extracted_data: editedData,
        }),
      });

      if (response.ok) {
        setSaveAlert({ type: "success", message: t("venue.documents.details.updateSuccess") });
        setEditMode(false);
        await loadProcessingResults();
      } else {
        const error = await response.json().catch(() => ({ error: "Unknown error" }));
        console.error("Save failed:", response.status, error);
        setSaveAlert({ type: "error", message: error.error || t("venue.documents.details.updateFailed") });
      }
    } catch (error) {
      console.error("Failed to save extracted data:", error);
      setSaveAlert({ type: "error", message: t("venue.documents.details.updateFailed") });
    } finally {
      setLoading(false);
    }
  };

  const renderCustomFormatData = (rawOutput, format) => {
    const formatUpper = format.toUpperCase();

    // Function to copy to clipboard
    const copyToClipboard = () => {
      navigator.clipboard.writeText(rawOutput).then(() => {
        setSaveAlert({ type: "success", message: t("venue.documents.details.copiedToClipboard", "Copied to clipboard!") });
        setTimeout(() => setSaveAlert(null), 3000);
      });
    };

    // Function to download as file
    const downloadFile = () => {
      const extensions = {
        csv: "csv",
        json: "json",
        text: "txt",
        markdown: "md",
        table: "txt",
      };
      const ext = extensions[format] || "txt";
      const mimeTypes = {
        csv: "text/csv",
        json: "application/json",
        text: "text/plain",
        markdown: "text/markdown",
        table: "text/plain",
      };
      const mimeType = mimeTypes[format] || "text/plain";

      const blob = new Blob([rawOutput], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `${fullDocument?.file_name || "document"}-extracted.${ext}`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    return (
      <Container
        header={
          <Header
            variant="h3"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="copy" onClick={copyToClipboard}>
                  {t("venue.documents.details.copy", "Copy")}
                </Button>
                <Button iconName="download" onClick={downloadFile}>
                  {t("venue.documents.details.download", "Download")}
                </Button>
              </SpaceBetween>
            }
          >
            {t("venue.documents.details.extractedData")} - {formatUpper} {t("venue.documents.details.format", "Format")}
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Box>
            <Badge color="blue">{formatUpper}</Badge>
          </Box>
          {format === "csv" && (
            <Box>
              <pre
                style={{
                  backgroundColor: "#f4f4f4",
                  padding: "12px",
                  borderRadius: "4px",
                  overflow: "auto",
                  maxHeight: "500px",
                  fontSize: "0.9em",
                  lineHeight: "1.5",
                  whiteSpace: "pre",
                  fontFamily: "monospace",
                }}
              >
                {rawOutput}
              </pre>
            </Box>
          )}
          {format === "json" && (
            <Box>
              <pre
                style={{
                  backgroundColor: "#f4f4f4",
                  padding: "12px",
                  borderRadius: "4px",
                  overflow: "auto",
                  maxHeight: "500px",
                  fontSize: "0.9em",
                  lineHeight: "1.5",
                  whiteSpace: "pre",
                  fontFamily: "monospace",
                }}
              >
                {rawOutput}
              </pre>
            </Box>
          )}
          {format === "markdown" && (
            <Box>
              <div
                style={{
                  backgroundColor: "#f4f4f4",
                  padding: "12px",
                  borderRadius: "4px",
                  overflow: "auto",
                  maxHeight: "500px",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                }}
              >
                {rawOutput}
              </div>
            </Box>
          )}
          {format === "table" && (
            <Box>
              <pre
                style={{
                  backgroundColor: "#f4f4f4",
                  padding: "12px",
                  borderRadius: "4px",
                  overflow: "auto",
                  maxHeight: "500px",
                  fontSize: "0.9em",
                  lineHeight: "1.5",
                  whiteSpace: "pre",
                  fontFamily: "monospace",
                }}
              >
                {rawOutput}
              </pre>
            </Box>
          )}
          {format === "text" && (
            <Box>
              <div
                style={{
                  backgroundColor: "#f4f4f4",
                  padding: "12px",
                  borderRadius: "4px",
                  overflow: "auto",
                  maxHeight: "500px",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  lineHeight: "1.6",
                }}
              >
                {rawOutput}
              </div>
            </Box>
          )}
        </SpaceBetween>
      </Container>
    );
  };

  const renderExtractedData = () => {
    // Get latest processing result
    const latestResult = processingResults[0];
    if (!latestResult?.extracted_data) return null;

    const data = editMode ? editedData : latestResult.extracted_data;
    const customFormat = latestResult?.custom_format;

    // Check if this is a custom format result with raw_output
    if (customFormat && data.raw_output && !editMode) {
      return renderCustomFormatData(data.raw_output, customFormat);
    }

    // Filter out key_insights as it's shown separately
    const filteredData = Object.entries(data).filter(([key]) => key !== "key_insights" && key !== "raw_output");

    if (filteredData.length === 0) return null;

    if (editMode) {
      return (
        <Container
          header={
            <Header
              variant="h3"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={handleCancelEdit}>{t("venue.documents.details.cancelEdit")}</Button>
                  <Button variant="primary" onClick={handleSaveData} loading={loading}>
                    {t("venue.documents.details.saveChanges")}
                  </Button>
                </SpaceBetween>
              }
            >
              {t("venue.documents.details.extractedData")} ({t("venue.documents.details.editing")})
            </Header>
          }
        >
          <SpaceBetween size="m">
            {filteredData.map(([key, value]) => {
              const fieldLabel = key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

              // Render different input types based on field type
              if (typeof value === "object") {
                // Complex objects/arrays - use textarea with JSON
                return (
                  <FormField key={key} label={fieldLabel}>
                    <Textarea
                      value={JSON.stringify(value, null, 2)}
                      onChange={({ detail }) => {
                        try {
                          const parsed = JSON.parse(detail.value);
                          setEditedData({ ...editedData, [key]: parsed });
                        } catch (e) {
                          // Keep as string if not valid JSON
                          setEditedData({ ...editedData, [key]: detail.value });
                        }
                      }}
                      rows={5}
                    />
                  </FormField>
                );
              } else if (isDateField(key, value)) {
                // Date fields - use date picker
                return (
                  <FormField key={key} label={fieldLabel}>
                    <DatePicker
                      value={formatDateForPicker(String(value))}
                      onChange={({ detail }) => setEditedData({ ...editedData, [key]: detail.value })}
                      placeholder="YYYY-MM-DD"
                      openCalendarAriaLabel={(selectedDate) => "Choose date" + (selectedDate ? `, selected date is ${selectedDate}` : "")}
                      nextMonthAriaLabel="Next month"
                      previousMonthAriaLabel="Previous month"
                      todayAriaLabel="Today"
                    />
                  </FormField>
                );
              } else {
                // Simple text/number fields - use input
                return (
                  <FormField key={key} label={fieldLabel}>
                    <Input
                      value={value === null || value === undefined ? "" : String(value)}
                      onChange={({ detail }) => setEditedData({ ...editedData, [key]: detail.value })}
                    />
                  </FormField>
                );
              }
            })}
          </SpaceBetween>
        </Container>
      );
    }

    const items = filteredData.map(([key, value]) => ({
      label: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      value: formatValue(value),
    }));

    // Helper to download extracted data as JSON
    const downloadExtractedJson = () => {
      const jsonStr = JSON.stringify(Object.fromEntries(filteredData), null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `${fullDocument?.file_name || "document"}-extracted.json`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const copyExtractedJson = () => {
      const jsonStr = JSON.stringify(Object.fromEntries(filteredData), null, 2);
      navigator.clipboard.writeText(jsonStr).then(() => {
        setSaveAlert({ type: "success", message: t("venue.documents.details.copiedToClipboard") });
        setTimeout(() => setSaveAlert(null), 3000);
      });
    };

    return (
      <Container
        header={
          <Header
            variant="h3"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="copy" onClick={copyExtractedJson}>
                  {t("venue.documents.details.copy")}
                </Button>
                <Button iconName="download" onClick={downloadExtractedJson}>
                  {t("venue.documents.details.download")}
                </Button>
                <Button iconName="edit" onClick={handleEditData}>
                  {t("venue.documents.details.editData")}
                </Button>
              </SpaceBetween>
            }
          >
            {t("venue.documents.details.extractedData")}
          </Header>
        }
      >
        <KeyValuePairs columns={2} items={items} />
      </Container>
    );
  };

  const renderProcessingResult = () => {
    // Get latest processing result
    const latestResult = processingResults[0];
    if (!latestResult?.summary) return null;

    return (
      <Container header={<Header variant="h3">{t("venue.documents.details.aiSummary")}</Header>}>
        <Box variant="p">{latestResult.summary}</Box>
      </Container>
    );
  };

  const renderKeyInsights = () => {
    // Get latest processing result
    const latestResult = processingResults[0];
    if (!latestResult?.key_insights) return null;

    const insights = latestResult.key_insights;
    if (!Array.isArray(insights) || insights.length === 0) return null;

    return (
      <Container header={<Header variant="h3">{t("venue.documents.details.keyInsights")}</Header>}>
        <SpaceBetween size="xs">
          {insights.map((insight, index) => (
            <Box key={index} variant="p">
              • {insight}
            </Box>
          ))}
        </SpaceBetween>
      </Container>
    );
  };

  if (!document) return null;

  const doc = fullDocument || document;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={<Header variant="h2">{doc.file_name}</Header>}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {doc.download_url && (
              <Button href={doc.download_url} target="_blank" iconName="download" wrapText={false}>
                <span style={{ whiteSpace: "nowrap" }}>{t("venue.common.download")}</span>
              </Button>
            )}
            <Button variant="primary" onClick={onDismiss} wrapText={false}>
              <span style={{ whiteSpace: "nowrap" }}>{t("venue.common.close")}</span>
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {loading ? (
        <Box textAlign="center" padding="l">
          {t("venue.documents.details.loadingDetails")}
        </Box>
      ) : (
        <SpaceBetween size="l">
          {saveAlert && (
            <Alert type={saveAlert.type} dismissible onDismiss={() => setSaveAlert(null)}>
              {saveAlert.message}
            </Alert>
          )}
          <Container header={<Header variant="h3">{t("venue.documents.details.documentInformation")}</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <KeyValuePairs
                columns={1}
                items={[
                  { label: "File name", value: doc.file_name },
                  {
                    label: "File type",
                    value: <Badge color="blue">{doc.file_name.split(".").pop().toUpperCase()}</Badge>,
                  },
                  { label: "File size", value: formatFileSize(doc.file_size) },
                  { label: "Uploaded", value: formatDate(doc.upload_date) },
                  {
                    label: "Document type",
                    value: doc.document_type_id ? (
                      <SpaceBetween direction="horizontal" size="xs">
                        <Badge color="green">{documentTypes[doc.document_type_id] || doc.document_type_id}</Badge>
                        {doc.type_auto_detected && <Badge>{t("venue.common.autoDetected")}</Badge>}
                      </SpaceBetween>
                    ) : (
                      "-"
                    ),
                  },
                ]}
              />
              <KeyValuePairs
                columns={1}
                items={[
                  { label: "Status", value: getStatusIndicator(doc.processing_status) },
                  {
                    label: "Processing started",
                    value: formatDate(doc.processing_started),
                  },
                  {
                    label: "Processing completed",
                    value: formatDate(doc.processing_completed),
                  },
                  {
                    label: "Confidence",
                    value: processingResults[0]?.confidence || "-",
                  },
                  {
                    label: "Processing attempts",
                    value: processingResults.length || 0,
                  },
                ]}
              />
            </ColumnLayout>
          </Container>

          {doc.description && (
            <Container header={<Header variant="h3">{t("venue.documents.details.description")}</Header>}>
              <Box variant="p">{doc.description}</Box>
            </Container>
          )}

          {renderProcessingResult()}
          {renderExtractedData()}
          {renderKeyInsights()}

          {doc.processing_error && doc.processing_status !== "completed" && (
            <Container header={<Header variant="h3">{t("venue.documents.details.processingError")}</Header>}>
              <Box variant="p" color="text-status-error">
                {doc.processing_error}
              </Box>
            </Container>
          )}

          {processingResults.length > 1 && (
            <ExpandableSection headerText={t("venue.documents.details.processingHistory", { count: processingResults.length })} variant="container">
              <SpaceBetween size="m">
                {processingResults.map((result, index) => (
                  <Container
                    key={result.result_id}
                    header={
                      <Header variant="h4">
                        {t("venue.documents.details.attempt", { number: processingResults.length - index })} - {formatDate(result.processed_date)}
                      </Header>
                    }
                  >
                    <SpaceBetween size="s">
                      <KeyValuePairs
                        columns={3}
                        items={[
                          { label: t("venue.documents.details.confidence"), value: result.confidence || "-" },
                          { label: t("venue.documents.details.model"), value: result.model_id?.split("/").pop() || "-" },
                          { label: t("venue.documents.details.resultId"), value: result.result_id },
                        ]}
                      />
                      {result.summary && (
                        <Box>
                          <Box variant="awsui-key-label">{t("venue.documents.details.summary")}</Box>
                          <Box variant="p">{result.summary}</Box>
                        </Box>
                      )}
                    </SpaceBetween>
                  </Container>
                ))}
              </SpaceBetween>
            </ExpandableSection>
          )}

          {doc.s3_key && (
            <ExpandableSection headerText="Technical Details" variant="footer">
              <KeyValuePairs
                columns={2}
                items={[
                  { label: "Document ID", value: doc.id },
                  { label: "S3 Key", value: doc.s3_key },
                  { label: "Document Type ID", value: doc.document_type_id || "None" },
                  { label: "Latest Result ID", value: doc.latest_result_id || "None" },
                ]}
              />
            </ExpandableSection>
          )}
        </SpaceBetween>
      )}
    </Modal>
  );
}

export default DocumentDetails;
